var _a;
import { __decorate, __metadata, __param } from "tslib";
import Vue from 'vue';
import _ from 'lodash';
import { treeToListByDFS } from '@cores/utils/listTree';
import { inject, provide } from '@/inversify.config';
import { UserCapacityService } from '@/domains/user/services/user-capacity-service.interface';
const TRANSFORM_STATUE_MAP = {
    READY: '000',
    SKIP: '100',
    FAIL_EXCEPTION: '501',
    FAIL_IO_EXCEPTION: '502',
    FAIL_RETRY_OVER_LIMIT: '503',
    PROCESSING: '300',
    FINISH: '200',
    FINISH_PART: '201',
    TIMEOUT: '408',
    CANCELED: '403',
};
const TRANSFORM_STATUS = {
    SUCCESS: [
        TRANSFORM_STATUE_MAP.FINISH,
        TRANSFORM_STATUE_MAP.FINISH_PART,
    ],
    PENDING: [
        TRANSFORM_STATUE_MAP.READY,
        TRANSFORM_STATUE_MAP.PROCESSING,
    ],
};
export const FieldTransformServiceSymbol = Symbol.for('FieldTransformService');
let FieldTransformService = class FieldTransformService {
    constructor(userCapacityService) {
        this.userCapacityService = userCapacityService;
        this.blackList = ['DATE', 'NUM', 'LAYER', 'URL']; // 不支持转换的类型
        this.TRANSFORM_STATUS = TRANSFORM_STATUS;
        this.transformMap = {
            STRING: ['OPTION', 'LAYER'],
            OPTION: ['LAYER'],
        };
        this.fieldsGroupOrder = ['OPTION', 'LAYER'];
        this.maxPatentRangeCount = 100000;
        this.folder = null;
        this.showTransformDialog = false;
        this.showResultDialog = false;
        this.remove = null;
        this.poll = null;
        this.isOriginFinish = false;
        this.tasks = [];
        this.currentShowTask = null;
        this.init({});
    }
    get limitPatentsCount() {
        return this.userCapacityService.getUserCapacityByKey('max_op');
    }
    get isLooping() {
        return !!this.remove;
    }
    init({ folder, poll }) {
        this.folder = folder;
        this.showTransformDialog = false;
        this.showResultDialog = false;
        this.remove = null;
        this.poll = poll;
        this.isOriginFinish = false;
        this.tasks = [];
        this.currentShowTask = null;
        return this;
    }
    onLoopError(error) {
        console.warn('onLoopError:', error);
    }
    createTransformPendingStatus() {
        Vue.set(this.folder, 'field_transform_infos', [{
                status_code: TRANSFORM_STATUE_MAP.PROCESSING,
            }]);
    }
    finishTransform() {
        const nextFinishTask = this.finishTask();
        if (nextFinishTask) {
            return setTimeout(() => {
                this.showFinishTask(nextFinishTask);
            }, 1000);
        }
        if (!this.isOriginFinish) {
            // 刷新页面
            window.Bus.$emit('SpaceDetail:RefreshPatents');
        }
        else if (this.findPendingTask()) {
            return this.start();
        }
    }
    handlerInfoChange(infos, isOrigin = false) {
        this.tasks = infos || [];
        const finishTask = this.findFinishTask();
        if (!this.findPendingTask() && !finishTask) {
            return true;
        }
        if (finishTask) {
            this.remove && this.remove();
            this.remove = null;
            Vue.set(this.folder, 'field_transform_infos', infos);
            // 如果进入页面第一次请求就有结果了，在关闭结果弹窗时，不刷新页面;
            if (isOrigin) {
                this.isOriginFinish = true;
            }
            this.showFinishTask(finishTask);
            return true;
        }
        console.info('[Transform Field] ==> Transform pending...');
    }
    updateFolder({ folder, isOrigin }) {
        this.folder = folder;
        if (this.handlerInfoChange(_.get(folder, 'field_transform_infos'), isOrigin)) {
            return;
        }
        this.start();
    }
    start() {
        // 只要开始循环，则不是初始就成功，关闭结果弹窗时，刷新页面;
        this.isOriginFinish = false;
        if (this.remove)
            return console.warn('[Loop exist]: TransformCustomField');
        this.remove = this.poll.addHook((folder) => {
            this.handlerInfoChange(folder.field_transform_infos, false);
        }, 'TransformCustomField');
    }
    stop() {
        this.remove && this.remove();
        this.remove = null;
        this.showTransformDialog = false;
    }
    canTransformField(data) {
        const field_type = _.get(data, 'column._fieldType');
        const is_custom = _.get(data, 'column._isCustom');
        const edit_value = _.get(data, 'column._props.edit_value');
        const is_white_list = !this.blackList.includes(field_type);
        return field_type && is_custom && edit_value && is_white_list;
    }
    transformFields(field, fields) {
        const { field_type } = field;
        const transformTypeList = this.transformMap[field_type] || [];
        const ableToTransformList = _.filter(fields, ({ field_type, edit_value }) => {
            return _.includes(transformTypeList, field_type) && edit_value;
        });
        return _.sortBy(ableToTransformList, [
            ({ field_type }) => {
                return this.fieldsGroupOrder.indexOf(field_type);
            },
            ({ created_at }) => {
                return 0 - Number(new Date(created_at));
            },
        ]);
    }
    transformLayerStructure(treeData) {
        const tree = _.cloneDeep(treeData);
        const nodes = treeToListByDFS(tree);
        const groupByNameMap = _.groupBy(nodes, 'name');
        const conflictList = _.compact(_.map(groupByNameMap, (nodes, nodeName) => {
            if (nodes.length > 1) {
                return {
                    nodeName,
                    defaultExpandedKeys: _.map(nodes, i => i.id),
                    nodes,
                    selectedNodeKey: nodes[0].id,
                    get selectedNode() {
                        return _.find(nodes, { id: this.selectedNodeKey });
                    },
                };
            }
            return null;
        }));
        return {
            data: tree,
            conflictList,
        };
    }
    finishTask() {
        this.showResultDialog = false;
        this.tasks = _.filter(this.tasks, ({ task_id }) => {
            return task_id !== this.currentShowTask.task_id;
        });
        Vue.set(this.folder, 'field_transform_infos', this.tasks);
        return this.findFinishTask();
    }
    findFinishTask() {
        return _.find(this.tasks, ({ status_code }) => {
            return !TRANSFORM_STATUS.PENDING.includes(status_code);
        });
    }
    findPendingTask() {
        return _.find(this.tasks, ({ status_code }) => {
            return TRANSFORM_STATUS.PENDING.includes(status_code);
        });
    }
    showFinishTask(finishTask) {
        this.currentShowTask = finishTask;
        this.showResultDialog = true;
    }
};
FieldTransformService = __decorate([
    provide(FieldTransformServiceSymbol),
    __param(0, inject(UserCapacityService)),
    __metadata("design:paramtypes", [typeof (_a = typeof UserCapacityService !== "undefined" && UserCapacityService) === "function" ? _a : Object])
], FieldTransformService);
export { FieldTransformService };
